import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  CloudArrowUp,
  CheckCircle,
  FileText,
  XCircle,
  ArrowsClockwise,
  ArrowCounterClockwise,
  Warning,
} from '@phosphor-icons/react';
import { formatDate } from '../../lib/utils';

/**
 * Sandata SFTP tab — summary cards, batch submit, response log, failed-visit resubmit.
 */
export const SandataTab = ({
  loading,
  status,
  responses,
  failedVisits,
  validatedCount,
  submitting,
  onSubmitBatch,
  onResubmitFailed,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatusCard
          icon={CloudArrowUp}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          label="Mode"
          value={status?.mode === 'mock' ? 'Demo' : 'Live'}
          valueClass="text-lg font-bold"
        />
        <StatusCard
          icon={CheckCircle}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Ready to Submit"
          value={validatedCount}
        />
        <StatusCard
          icon={FileText}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Batches Sent"
          value={responses.length}
        />
        <StatusCard
          icon={XCircle}
          iconBg="bg-red-50"
          iconColor="text-red-600"
          label="Failed Visits"
          value={failedVisits.length}
        />
      </div>

      {/* Submit button */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">
                Submit Validated Visits to Sandata
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                All validated EVV visits will be uploaded via SFTP
              </p>
            </div>
            <Button
              onClick={onSubmitBatch}
              disabled={submitting || validatedCount === 0}
              className="bg-indigo-900 hover:bg-indigo-800"
              data-testid="submit-sandata-batch"
            >
              {submitting ? (
                <ArrowsClockwise size={18} className="mr-2 animate-spin" />
              ) : (
                <CloudArrowUp size={18} className="mr-2" />
              )}
              Submit to Sandata
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Responses */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Submission Responses</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {responses.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No submissions yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch ID</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Accepted</TableHead>
                  <TableHead>Rejected</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {responses.map((r) => (
                  <TableRow key={r.batch_id}>
                    <TableCell className="font-mono text-sm">{r.batch_id}</TableCell>
                    <TableCell>{formatDate(r.processed_at)}</TableCell>
                    <TableCell>{r.total_visits}</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-50 text-emerald-700">{r.accepted}</Badge>
                    </TableCell>
                    <TableCell>
                      {r.rejected > 0 ? (
                        <Badge className="bg-red-50 text-red-700">{r.rejected}</Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.rejected === 0 ? (
                        <Badge className="bg-emerald-50 text-emerald-700">
                          <CheckCircle size={12} className="mr-1" />
                          Complete
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700">
                          <Warning size={12} className="mr-1" />
                          Has Errors
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Failed visits */}
      {failedVisits.length > 0 && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-red-800">
                Failed Visits - Need Correction
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResubmitFailed(failedVisits.map((v) => v.visit_id))}
                className="border-red-300 text-red-700 hover:bg-red-100"
                data-testid="resubmit-failed"
              >
                <ArrowCounterClockwise size={16} className="mr-2" />
                Resubmit All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Visit ID</TableHead>
                  <TableHead>Error Code</TableHead>
                  <TableHead>Error Message</TableHead>
                  <TableHead>Suggested Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedVisits.map((v) => (
                  <TableRow key={v.visit_id}>
                    <TableCell className="font-mono text-sm">{v.visit_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-red-50 text-red-700">
                        {v.error_code}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{v.error_message}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {v.suggested_action}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

const StatusCard = ({ icon: Icon, iconBg, iconColor, label, value, valueClass }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <Icon size={24} className={iconColor} />
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className={valueClass || 'text-2xl font-bold text-slate-900'}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);
